/**
 * The price list, and starting a purchase.
 *
 * `GET /api/billing/plans/` answers in numbers and identifiers only — no copy.
 * Every word a customer reads on the plans screen comes from the `plans`
 * translation namespace, keyed by the identifiers below, which is why nothing
 * here carries a label and why a price is never written down in the frontend.
 * `backend/src/licensing_app/plans.py` is the single source of truth for what
 * things cost.
 */

import { API_CONFIG } from '@/config/api'
import type { Edition } from '@/types/auth'
import { baseService } from './baseService'

/** The three things somebody can be on, in the order they are sold. */
export type PlanId = 'facewise' | 'facewise_pro' | 'facewise_enterprise'

/**
 * One thing somebody can buy. `period` is null for a one-off package — bought
 * once, spent at whatever pace suits — and 'month' for a subscription.
 */
export interface PlanPackage {
  id: string
  plan: PlanId
  respondents: number
  /** What leaves the card once, for the whole period. */
  price_pln: number
  /** The same money per month — what the cards are compared on. */
  price_per_month_pln: number
  /** How many months one purchase covers: 1 or 3. */
  months: number
  /** How much cheaper the month is than on the monthly package. 0 on that one. */
  discount_percent: number
  period: 'month' | null
  /** The package the plan leans on; at most one per plan. */
  recommended: boolean
}

export interface Plan {
  id: PlanId
  edition: Edition
  /** False means the plan is agreed with a person, not bought from a page. */
  self_serve: boolean
  /**
   * False means the plan is announced but not released — the card, the price
   * and the feature list are all shown, and only the buying is refused (the
   * checkout endpoint answers 409 `plan_not_on_sale`). Optional so a build that
   * meets an older backend treats every plan as buyable, which is how it
   * behaved before staged launches existed.
   */
  on_sale?: boolean
  /** 30 on the entry plan, null on the ones a trial turns into. */
  trial_days: number | null
  /** Capability keys translated through `plans.json` — never copy. */
  includes: string[]
  packages: PlanPackage[]
  from_price_pln: number | null
}

/**
 * What the account has running at the payment provider, if anything.
 *
 * Null for every licence that never went through a checkout — the ones agreed
 * on a contract and entered by hand — and that is the point: ending one of
 * those is a conversation, not a button.
 */
export interface SubscriptionState {
  active: boolean
  /** Cancelled, but paid up. Access runs to `ends_at` either way. */
  cancel_at_period_end: boolean
  ends_at: string | null
  /**
   * When the card is charged next (Z-70). Not the same as `ends_at`: buying
   * during a trial carries the unused days over, so access can run past the
   * charge. Null on older accounts and once the subscription has ended.
   */
  next_charge_at?: string | null
}

/** Where this account sits in the list above. */
export interface CurrentStanding {
  plan: PlanId
  edition: Edition
  /** Whether the 30-day clock is still what is keeping the lights on. */
  on_trial: boolean
  days_left: number | null
  has_access: boolean
  respondents: {
    /** null when no package has been set on the account at all. */
    limit: number | null
    used: number
    remaining: number | null
    /**
     * The window the count belongs to: when it refills, and what the pace is
     * measured against. Null for an allowance spent once rather than monthly,
     * and optional so a build that meets an older backend draws no forecast.
     */
    period_start?: string | null
    period_end?: string | null
  }
  /** Optional so a build that meets an older backend simply shows nothing. */
  subscription?: SubscriptionState | null
}

/** The card Stripe charges, as much of it as is safe to show. */
export interface PaymentCard {
  brand: string
  last4: string
  exp_month: number | null
  exp_year: number | null
}

/** One invoice from Stripe. Amounts are in the smallest unit (grosze). */
export interface BillingInvoice {
  id: string
  number: string
  date: string | null
  amount: number
  currency: string
  status: 'paid' | 'open' | 'uncollectible' | string
  pdf_url: string
  url: string
}

/**
 * The money side of the account, read live from Stripe (Z-126).
 *
 * Every field is empty for an account that never went through a checkout —
 * a trial, a licence entered by hand — which the screen draws as "nothing
 * here yet" rather than as an error.
 */
export interface BillingAccount {
  payment_method: PaymentCard | null
  /** The next charge with VAT, or null when nothing more will be taken. */
  upcoming: { amount: number; currency: string; date: string | null } | null
  invoices: BillingInvoice[]
}

export interface PlansCatalogue {
  plans: Plan[]
  current: CurrentStanding
  /**
   * False means pressing buy records the request and emails sales instead of
   * opening a checkout. The screen says so before anybody presses anything.
   */
  checkout_available: boolean
  sales_email: string
}

/**
 * What starting a purchase can come back as.
 *
 * `recorded` is the honest answer while no payment provider is connected: the
 * request exists, nobody has been charged, and a person grants the package by
 * hand. It is deliberately not the same shape as a completed purchase.
 */
export type CheckoutResult =
  | { status: 'checkout'; url: string; intent_id: number }
  | { status: 'recorded'; intent_id: number }

export class BillingService {
  async getPlans(): Promise<PlansCatalogue> {
    const response = await baseService.get<PlansCatalogue>(API_CONFIG.ENDPOINTS.BILLING.PLANS)
    return response.data
  }

  async getAccount(): Promise<BillingAccount> {
    const response = await baseService.get<BillingAccount>(API_CONFIG.ENDPOINTS.BILLING.ACCOUNT)
    return response.data
  }

  async startCheckout(packageId: string): Promise<CheckoutResult> {
    const response = await baseService.post<CheckoutResult>(
      API_CONFIG.ENDPOINTS.BILLING.CHECKOUT,
      { package_id: packageId },
    )
    return response.data
  }

  /**
   * Stop the renewal. Nothing is refunded and nothing is taken away — the
   * period already paid for runs to its end, which is what `ends_at` says.
   */
  async cancelSubscription(): Promise<SubscriptionState | null> {
    const response = await baseService.post<{ subscription: SubscriptionState | null }>(
      API_CONFIG.ENDPOINTS.BILLING.CANCEL_SUBSCRIPTION,
      {},
    )
    return response.data.subscription
  }

  /** Undo a cancellation that has not run out yet. */
  async resumeSubscription(): Promise<SubscriptionState | null> {
    const response = await baseService.post<{ subscription: SubscriptionState | null }>(
      API_CONFIG.ENDPOINTS.BILLING.RESUME_SUBSCRIPTION,
      {},
    )
    return response.data.subscription
  }

  /**
   * Open the Stripe customer portal, so the customer can swap their own card
   * (Z-95). Resolves to the URL to redirect the browser to; the caller does
   * the redirect, since leaving the app is a decision the screen makes, not
   * the service.
   */
  async openBillingPortal(): Promise<string> {
    const response = await baseService.post<{ url: string }>(
      API_CONFIG.ENDPOINTS.BILLING.PORTAL,
      {},
    )
    return response.data.url
  }
}

/** Backend error codes the screen reacts to by name rather than by message. */
export type BillingErrorCode = 'unknown_package' | 'plan_not_on_sale' | 'no_stripe_customer'

export function getBillingErrorCode(error: unknown): BillingErrorCode | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code?: BillingErrorCode }).code
  }
  return undefined
}

export const billingService = new BillingService()
